import React from 'react';
import {Box, Typography, Tooltip} from '@mui/material';
import { BAND_COLOR } from '../constants';

import {BandChip as SharedBandChip} from '../../DashboardCommon/widgets.comp';
import {fmtValue, benchmarkText} from '../../DashboardCommon/units';
export const NAVY   = '#052F44';
export const ORANGE = '#FF6900';
export const TEAL   = '#1B6B55';
export const AMBER  = '#EFA020';
// Primary benchmark-marker colour (Class average). Unlike NAVY (which never
// changes for dark mode), --brand-text actually flips to a light colour in
// dark mode — these markers are plain <Box> border-colors, not Typography,
// so they're invisible to the global dark-mode Typography override and need
// their own adaptive reference or they go pitch-black-on-near-black.
export const MARKER_PRIMARY = 'var(--brand-text, #052F44)';

export const bandColor = (label, bandColorMap) => {
    const map = bandColorMap || BAND_COLOR;
    return map[label] || BAND_COLOR[label] || '#6B7280';
};

/* Solid pill chip */
export const BandChip = ({label, color, bandColorMap}) => <SharedBandChip label={label} color={color || bandColor(label, bandColorMap)}/>;

/* Page header — title · subtitle */
export const PageHeader = ({title, subtitle}) => (
    <Box sx={{mb: 3}}>
        <Typography sx={{fontSize: {xs: '22px', md: '23px'}, fontWeight: 800, color: NAVY, lineHeight: 1.2}}>
            {title}
        </Typography>
        {subtitle && (
            <Typography sx={{fontSize: '14px', fontStyle: 'italic', color: '#4B5563', mt: 0.75}}>{subtitle}</Typography>
        )}
    </Box>
);

/* Decision-guidance banner — matches the bluish decision-guidance card used
   across the other dashboards: navy left border, all corners rounded,
   blue-tinted background, separate bold heading (not an inline label). */
export const GuidanceBanner = ({label = 'Decision guidance:', text}) => (
    <Box sx={{
        mt: 3, border: '1px solid #C7D9E8', borderLeft: `4px solid ${NAVY}`, borderRadius: '8px',
        backgroundColor: 'var(--brand-surface-subtle, #F0F6FB)', p: 2.5,
    }}>
        <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, mb: 1}}>
            {label}
        </Typography>
        <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>
            {text}
        </Typography>
    </Box>
);

/* KPI stat card — label · value · sub · band pill */
export const StatCard = ({label, value, sub, band, bandColorOverride, topColor, bandColorMap}) => {
    const resolvedTopColor = topColor || (band ? (bandColorOverride || bandColor(band, bandColorMap)) : null);
    return (
        <Box sx={{
            border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden', height: '100%',
            display: 'flex', flexDirection: 'column',
        }}>
            {resolvedTopColor && <Box sx={{backgroundColor: resolvedTopColor, height: '6px', flexShrink: 0}}/>}
            <Box sx={{p: 2.5, display: 'flex', flexDirection: 'column', flex: 1}}>
                <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '1px', textTransform: 'uppercase', mb: 1}}>
                    {label}
                </Typography>
                <Typography sx={{fontSize: {xs: '32px', md: '40px'}, fontWeight: 800, color: NAVY, lineHeight: 1}}>
                    {value}
                </Typography>
                {sub && <Typography sx={{fontSize: '12px', color: '#6A7282', mt: 0.75}}>{sub}</Typography>}
                {band && (
                    <Box sx={{mt: 'auto', pt: 1.5}}>
                        <BandChip label={band} color={bandColorOverride} bandColorMap={bandColorMap}/>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

/* Legend — rubric band chips (e.g. High/Moderate/Basic/Low) + "Class avg" marker line */
export const BenchmarkLegend = ({bandColorMap}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end', ml: 'auto'}}>
        {Object.entries(bandColorMap || BAND_COLOR).map(([label, color]) => (
            <BandChip key={label} label={label} color={color}/>
        ))}
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `7px solid ${MARKER_PRIMARY}`}}/>
            <Typography sx={{fontSize: '12px', color: '#6A7282'}}>Class avg</Typography>
        </Box>
    </Box>
);

/* Bar track (0–100) with fill + optional class-average marker (▽ above the bar) */
export const MarkerBar = ({score, color, marker, markerLabel = 'Class avg', height = 16}) => (
    <Box sx={{position: 'relative', flex: 1, height: `${height}px`, backgroundColor: 'var(--brand-surface-muted, #F3F4F6)', borderRadius: `${height / 2}px`}}>
        <Tooltip title={fmtValue(score)} arrow placement="top">
            <Box sx={{position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(100, score)}%`, backgroundColor: color, borderRadius: `${height / 2}px`, cursor: 'pointer'}}/>
        </Tooltip>
        {marker != null && (
            <Tooltip title={benchmarkText(markerLabel, marker)} arrow placement="top">
                <Box sx={{
                    position: 'absolute', left: `${marker}%`, top: '-9px', width: 0, height: 0,
                    borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
                    borderTop: `9px solid ${MARKER_PRIMARY}`,
                    transform: 'translateX(-50%)', cursor: 'pointer', zIndex: 4,
                }}/>
            </Tooltip>
        )}
    </Box>
);

/* Score row — label + bar (with class-average marker) + value + band chip */
export const ScoreRow = ({area, label, sub, score, band, marker, markerLabel, labelWidth = {xs: 160, md: 240}, bandColorMap, layout}) => {
    if (layout === 'stacked') {
        return (
            <Box sx={{mb: 2.5}}>
                {area && <Typography sx={{fontSize: '11.5px', color: '#6A7282', mb: 0.25}}>{area}</Typography>}
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                    <Typography sx={{fontSize: '13.5px', fontWeight: 700, color: NAVY, lineHeight: 1.3}}>{label}</Typography>
                    <Typography sx={{fontSize: '15px', fontWeight: 800, color: NAVY}}>{score}</Typography>
                </Box>
                {sub && <Typography sx={{fontSize: '11px', color: '#9CA3AF', mb: 0.5}}>{sub}</Typography>}
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                    <MarkerBar score={score} color={bandColor(band, bandColorMap)} marker={marker} markerLabel={markerLabel}/>
                    <Box sx={{flexShrink: 0}}><BandChip label={band} bandColorMap={bandColorMap}/></Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2.5}}>
            <Box sx={{width: labelWidth, flexShrink: 0}}>
                {area && <Typography sx={{fontSize: '12px', color: '#6A7282'}}>{area}</Typography>}
                <Typography sx={{fontSize: '13.5px', fontWeight: 700, color: NAVY, lineHeight: 1.3}}>{label}</Typography>
                {sub && <Typography sx={{fontSize: '11px', color: '#9CA3AF'}}>{sub}</Typography>}
            </Box>
            <MarkerBar score={score} color={bandColor(band, bandColorMap)} marker={marker} markerLabel={markerLabel}/>
            <Typography sx={{fontSize: '15px', fontWeight: 800, color: NAVY, width: 30, textAlign: 'right', flexShrink: 0}}>{score}</Typography>
            <Box sx={{flexShrink: 0}}><BandChip label={band} bandColorMap={bandColorMap}/></Box>
        </Box>
    );
};

/* Strengths / Areas-to-grow list card (coloured top bar) */
// emptyText: shown when the list is empty, so the card is never blank (ID slide 79).
export const ListCard = ({title, items, color, emptyText}) => (
    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden', height: '100%'}}>
        <Box sx={{backgroundColor: color, height: '6px'}}/>
        <Box sx={{p: 3}}>
            <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 1.5}}>{title}</Typography>
            {items.length === 0 && emptyText && (
                <Typography sx={{fontSize: '13.5px', color: '#6B7280', lineHeight: 1.5}}>{emptyText}</Typography>
            )}
            <Box component="ul" sx={{m: 0, pl: 2.5}}>
                {items.map((it) => (
                    <Box component="li" key={it} sx={{mb: 1}}>
                        <Typography sx={{fontSize: '13.5px', color: '#374151', lineHeight: 1.5}}>{it}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    </Box>
);
